import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Divider,
  Grid,
  MenuItem,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import ByzMuiFormSelect from "src/components/core-components/mui/ByzMuiFormSelect";
import ByzSelectListPopupInput from "src/components/core-components/mui/inputs/ByzSelectListPopupInput";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import { ByzMuiCheckBox } from "src/components/core-components/mui/ByzMuiCheckBox";
import ByzMuiButton from "src/components/core-components/mui/buttons/ByzMuiButton";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { WillOpenCardKey } from "src/api/request/requestService";
import { referenceService } from "src/api/references/referenceService";
import { useAppSelector } from "src/app/hooks";
import { RootState } from "src/app/store";
import { axiosInstance } from "src/api/config/axiosInstance";
import { useTranslate } from "src/locales";

interface PatientComingFileChangeModalProps {
  open: boolean;
  onClose: () => void;
  serviceName?: string;
  onSave?: () => void;
  // Merkezi additional-info fetch için opsiyonel parametreler
  additionalInfoKey?: WillOpenCardKey;
  additionalInfoParams?: {
    serviceId: number;
    requestDetailId: number;
    ptId?: number;
    officeId?: number;
  };
  editOperationMode?: boolean;
  onDoctorSelected?: (data: {
    arrivalTypeId: string;
    departmentId: string;
    doctorId: string;
    doctorName: string;
    doctorRoleId: string;
    doctorRoleName: string;
  }) => void;
}

const PatientComingFileChangeModal: React.FC<PatientComingFileChangeModalProps> = ({
  open,
  onClose,
  serviceName = "Seçilen Hizmet Adı",
  onSave,
  additionalInfoKey,
  additionalInfoParams,
  editOperationMode,
  onDoctorSelected,
}) => {
  const { t } = useTranslate();
  const [arrivalType, setArrivalType] = useState("");
  const [departmentName, setDepartmentName] = useState("");
  const [doctorName, setDoctorName] = useState("");
  const [doctorRole, setDoctorRole] = useState("");
  const [referenceDoctor, setReferenceDoctor] = useState("");
  const [isReferenceDoctor, setIsReferenceDoctor] = useState(false);
  const [assistantDoctor, setAssistantDoctor] = useState("");
  const [referringPersonnel, setReferringPersonnel] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState<any>(null);
  const [arrivalOptions, setArrivalOptions] = useState<{ label: string; value: string }[]>([]);
  const [loadingArrival, setLoadingArrival] = useState(false);
  const [departmentOptions, setDepartmentOptions] = useState<{ label: string; value: string }[]>([]);
  const [loadingDept, setLoadingDept] = useState(false);
  const [doctorOptions, setDoctorOptions] = useState<{ label: string; value: string }[]>([]);
  const [loadingDoctor, setLoadingDoctor] = useState(false);
  const [doctorRoleOptions, setDoctorRoleOptions] = useState<{ label: string; value: string }[]>([]);
  const [loadingDoctorRole, setLoadingDoctorRole] = useState(false);
  const { selectedOfficeId } = useAppSelector((state: RootState) => ({
    selectedOfficeId: state.auth.selectedOfficeId,
  }));

  const resetAfterArrivalChange = () => {
    setDepartmentName("");
    setDoctorName("");
    setDoctorRole("");
    setReferenceDoctor("");
    setIsReferenceDoctor(false);
    setAssistantDoctor("");
    setReferringPersonnel("");
    setDepartmentOptions([]);
    setDoctorOptions([]);
  };

  const resetAfterDepartmentChange = () => {
    setDoctorName("");
    setDoctorRole("");
    setReferenceDoctor("");
    setIsReferenceDoctor(false);
    setAssistantDoctor("");
    setReferringPersonnel("");
    setDoctorOptions([]);
  };

  const resetAfterDoctorChange = () => {
    setDoctorRole("");
    setDoctorRoleOptions([]);
  };


  const assistantStaticOptions = useMemo(
    () => [
      { id: "asst1", NAME: "Asistan Doktor 1" },
      { id: "asst2", NAME: "Asistan Doktor 2" },
    ],
    []
  );

  const referringPersonnelStaticOptions = useMemo(
    () => [
      { id: "personnel1", NAME: "Personel 1" },
      { id: "personnel2", NAME: "Personel 2" },
    ],
    []
  );

  const selectedDoctorRoleLabel = useMemo(() => {
    const found = doctorRoleOptions.find((opt) => opt.value === doctorRole);
    return found?.label || "";
  }, [doctorRole, doctorRoleOptions]);

  const selectedAssistantLabel = useMemo(() => {
    const found = assistantStaticOptions.find((opt) => opt.id === assistantDoctor);
    return found?.NAME || "";
  }, [assistantDoctor, assistantStaticOptions]);

  const selectedReferringPersonnelLabel = useMemo(() => {
    const found = referringPersonnelStaticOptions.find((opt) => opt.id === referringPersonnel);
    return found?.NAME || "";
  }, [referringPersonnel, referringPersonnelStaticOptions]);

  // Seçili değerlerin label'larını hesapla
  const selectedArrivalTypeLabel = useMemo(() => {
    const found = arrivalOptions.find((opt) => opt.value === arrivalType);
    return found?.label || "";
  }, [arrivalType, arrivalOptions]);

  const selectedDepartmentLabel = useMemo(() => {
    const found = departmentOptions.find((opt) => opt.value === departmentName);
    return found?.label || "";
  }, [departmentName, departmentOptions]);

  const selectedDoctorLabel = useMemo(() => {
    const found = doctorOptions.find((opt) => opt.value === doctorName);
    return found?.label || "";
  }, [doctorName, doctorOptions]);

  const selectedReferenceDoctorLabel = useMemo(() => {
    const found = doctorOptions.find((opt) => opt.value === referenceDoctor);
    return found?.label || "";
  }, [referenceDoctor, doctorOptions]);

  const handleSave = () => {
    if (editOperationMode && onDoctorSelected) {
      onDoctorSelected({
        arrivalTypeId: arrivalType,
        departmentId: departmentName,
        doctorId: doctorName,
        doctorName: selectedDoctorLabel,
        doctorRoleId: doctorRole,
        doctorRoleName: selectedDoctorRoleLabel,
      });
      onClose();
      return;
    }
    
    onSave?.();
    if (!onSave) {
      onClose();
    }
  };

  // WillOpenCard KEY bazlı ek bilgi formunu merkezi servisten yükle
  useEffect(() => {
    if (!open || !additionalInfoKey) return;

    const loadAdditionalInfo = async () => {
      try {
        const res = await referenceService.additionalInfo.getFields({
          formtype: additionalInfoKey,
          offid:
            additionalInfoParams?.officeId ??
            (selectedOfficeId ? Number(selectedOfficeId) : undefined),
        });
        setAdditionalInfo(res);
        // TODO: Dönen alanlara göre geliş tipi / bölüm / doktor vb. doldurulabilir.
      } catch {
        // Hata durumunda mevcut manuel akış devam etsin.
      }
    };

    loadAdditionalInfo();
  }, [open, additionalInfoKey, additionalInfoParams, selectedOfficeId]);

  // Geliş tipi listesini AskDoctor API'sinden yükle (modal açılınca)
  useEffect(() => {
    if (!open) return;

    const fetchArrivalTypes = async () => {
      try {
        setLoadingArrival(true);
        const res = await axiosInstance.get("reference/api/AskDoctor/patient-coming-types");
        const list = (res as any)?.data?.data ?? (res as any)?.data ?? [];
        if (Array.isArray(list)) {
          setArrivalOptions(
            list.map((it: any) => ({
              label: it.name ?? "",
              value: String(it.id),
            }))
          );
        } else {
          setArrivalOptions([]);
        }
      } catch (err) {
        console.error("patient-coming-types load error", err);
        setArrivalOptions([]);
      } finally {
        setLoadingArrival(false);
      }
    };

    fetchArrivalTypes();
    // Bölüm Adı fetch'i Geliş Tipi seçilince tetiklenecek (aşağıdaki useEffect)
  }, [open]);

  // Geliş Tipi seçilince Bölüm Adı fetch'i tetikle
  useEffect(() => {
    const fetchDepartments = async () => {
      if (!arrivalType || !selectedOfficeId) {
        setDepartmentOptions([]);
        setDepartmentName(""); // Bölüm seçimini sıfırla
        setDoctorName(""); // Doktor seçimini de sıfırla
        setDoctorRole(""); // Doktor rolünü de sıfırla
        setDoctorOptions([]); // Doktor listesini temizle
        return;
      }
      try {
        setLoadingDept(true);
        // Bölüm Adı: reference/api/AskDoctor/departments
        // API: DT_ID, OFF_ID ve opsiyonel Service ID'ye göre departmanları getirir
        const res = await axiosInstance.post("reference/api/AskDoctor/departments", {
          dtid: Number(arrivalType), // Department Type ID (patient-coming-types'tan dönen id)
          offid: selectedOfficeId ? Number(selectedOfficeId) : 0,
          serviceid: additionalInfoParams?.serviceId ?? 0, // Opsiyonel: Branch Code filtresi için
        });
        // Response yapısı: { data: [...], issuccess: true, statuscode: 200, message: "" }
        const responseData = (res as any)?.data;
        const list = responseData?.data ?? [];
        if (Array.isArray(list)) {
          setDepartmentOptions(
            list
              .filter((d: any) => d.isactive === "1" || d.isactive === 1) // Sadece aktif departmanlar
              .map((d: any) => ({
                label: d.name ?? "",
                value: String(d.id),
              }))
          );
        } else {
          setDepartmentOptions([]);
        }
      } catch (err) {
        console.error("AskDoctor departments load error:", err);
        setDepartmentOptions([]);
      } finally {
        setLoadingDept(false);
      }
    };

    fetchDepartments();
  }, [arrivalType, selectedOfficeId, additionalInfoParams]);

  // Bölüm seçilince müsait doktorları getir
  useEffect(() => {
    const fetchDoctors = async () => {
      if (!departmentName || !selectedOfficeId) {
        setDoctorOptions([]);
        setDoctorName(""); // Doktor seçimini sıfırla
        setDoctorRole(""); // Doktor rolünü de sıfırla
        return;
      }
      try {
        setLoadingDoctor(true);
        // Müsait doktorları getir: reference/api/AskDoctor/available-doctors
        const res = await axiosInstance.post("reference/api/AskDoctor/available-doctors", {
          deptid: Number(departmentName), // Department ID
          offid: Number(selectedOfficeId), // Office ID
          date: new Date().toISOString(), // Opsiyonel: Tarih (varsayılan: şu anki zaman)
        });
        // Response yapısı: { data: [...], issuccess: true, statuscode: 200, message: "" }
        const responseData = (res as any)?.data;
        const list = responseData?.data ?? [];
        if (Array.isArray(list)) {
          setDoctorOptions(
            list.map((d: any) => ({
              label: `${d.drtitle || ""} ${d.name || ""} ${d.surname || ""}`.trim(),
              value: String(d.id),
            }))
          );
        } else {
          setDoctorOptions([]);
        }
      } catch (err) {
        console.error("available doctors load error", err);
        setDoctorOptions([]);
      } finally {
        setLoadingDoctor(false);
      }
    };
    fetchDoctors();
  }, [departmentName, selectedOfficeId]);

  useEffect(() => {
    const fetchDoctorRoles = async () => {
      if (!doctorName || !departmentName) {
        setDoctorRoleOptions([]);
        setDoctorRole("");
        return;
      }
      try {
        setLoadingDoctorRole(true);
        const res = await axiosInstance.post("reference/api/AskDoctor/roles", {
          drid: Number(doctorName),
          deptid: Number(departmentName),
        });
        const responseData = (res as any)?.data;
        const list = responseData?.data ?? [];
        if (Array.isArray(list)) {
          setDoctorRoleOptions(
            list.map((r: any) => ({
              label: r.name ?? "",
              value: String(r.id),
            }))
          );
        } else {
          setDoctorRoleOptions([]);
        }
      } catch (err) {
        console.error("doctor roles load error", err);
        setDoctorRoleOptions([]);
      } finally {
        setLoadingDoctorRole(false);
      }
    };
    fetchDoctorRoles();
  }, [doctorName, departmentName]);

  return (
    <OpenComponentDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      hideHeader
      showPatientData={false}
    >
      <Box
        sx={{
          mx: -2,
          mt: 4,
          mb: 2,
          py: 1.5,
          px: 2,
          backgroundColor: "#F4F7FE",
          borderBottom: "1px solid #E0E0E0",
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <Typography variant="h6" fontWeight={700} color="#111827">
            {t("patient-request.patient-file-change.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {serviceName}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          px: { xs: 1, sm: 1.5 },
          pb: { xs: 2, sm: 3 },
        }}
      >
        <Box
          sx={{
            mb: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <ByzMuiSaveButton 
            onClick={handleSave} 
            disabled={editOperationMode && (!doctorName || !doctorRole)}
          />
          <Box sx={{ flex: 1 }} />
          <ByzMuiCloseButton onClick={onClose} />
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t("patient-request.patient-file-change.arrival-type")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <Box sx={{ width: 320 }}>
              <ByzSelectListPopupInput
                fullWidth
                size="small"
              label={t("patient-request.patient-file-change.arrival-type")}
                value={selectedArrivalTypeLabel}
                staticData={arrivalOptions.map((opt) => ({
                  id: opt.value,
                  NAME: opt.label,
                }))}
                displayField="NAME"
                disabled={loadingArrival || arrivalOptions.length === 0}
                onChange={(rows) => {
                  const list = Array.isArray(rows) ? rows : (rows ? [rows] : []);
                  const first = list[0];
                  if (first) {
                    resetAfterArrivalChange();
                    setArrivalType(String(first.id));
                  } else {
                    resetAfterArrivalChange();
                    setArrivalType("");
                  }
                }}
                selectedId={arrivalType || undefined}
                dialogMaxHeight="60vh"
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t("patient-request.patient-file-change.department")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <Box sx={{ width: 320 }}>
              <ByzSelectListPopupInput
                fullWidth
                size="small"
              label={t("patient-request.patient-file-change.department")}
                value={selectedDepartmentLabel}
                staticData={departmentOptions.map((opt) => ({
                  id: opt.value,
                  NAME: opt.label,
                }))}
                displayField="NAME"
                disabled={
                  !arrivalType ||
                  loadingDept ||
                  departmentOptions.length === 0
                }
                onChange={(rows) => {
                  const list = Array.isArray(rows) ? rows : (rows ? [rows] : []);
                  const first = list[0];
                  if (first) {
                    resetAfterDepartmentChange();
                    setDepartmentName(String(first.id));
                  } else {
                    resetAfterDepartmentChange();
                    setDepartmentName("");
                  }
                }}
                selectedId={departmentName || undefined}
                dialogMaxHeight="60vh"
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t("patient-request.patient-file-change.doctor-role")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Box sx={{ width: 320 }}>
                <ByzSelectListPopupInput
                  fullWidth
                  size="small"
                  label={t("patient-request.patient-file-change.doctor")}
                  value={selectedDoctorLabel}
                  staticData={doctorOptions.map((opt) => ({
                    id: opt.value,
                    NAME: opt.label,
                  }))}
                  displayField="NAME"
                  disabled={
                    !departmentName ||
                    loadingDoctor ||
                    doctorOptions.length === 0
                  }
                  onChange={(rows) => {
                    const list = Array.isArray(rows) ? rows : (rows ? [rows] : []);
                    const first = list[0];
                    if (first) {
                    resetAfterDoctorChange();
                      setDoctorName(String(first.id));
                    } else {
                    resetAfterDoctorChange();
                      setDoctorName("");
                    }
                  }}
                selectedId={doctorName || undefined}
                  dialogMaxHeight="60vh"
                />
              </Box>
              <Box sx={{ width: 140 }}>
                <ByzSelectListPopupInput
                  fullWidth
                  size="small"
                  label={t("patient-request.patient-file-change.role")}
                  value={selectedDoctorRoleLabel}
                  staticData={doctorRoleOptions.map((opt) => ({
                    id: opt.value,
                    NAME: opt.label,
                  }))}
                  displayField="NAME"
                  disabled={!doctorName || loadingDoctorRole || doctorRoleOptions.length === 0}
                  onChange={(rows) => {
                    const list = Array.isArray(rows) ? rows : (rows ? [rows] : []);
                    const first = list[0];
                    if (first) {
                      setDoctorRole(String(first.id));
                    } else {
                      setDoctorRole("");
                    }
                  }}
                  selectedId={doctorRole || undefined}
                  dialogMaxHeight="60vh"
                />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t("patient-request.patient-file-change.reference-doctor")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Box sx={{ width: 320 }}>
                <ByzSelectListPopupInput
                  fullWidth
                  size="small"
                  label={t("patient-request.patient-file-change.reference-doctor")}
                  value={selectedReferenceDoctorLabel}
                  staticData={doctorOptions.map((opt) => ({
                    id: opt.value,
                    NAME: opt.label,
                  }))}
                  displayField="NAME"
                  disabled={
                    editOperationMode ||
                    !departmentName ||
                    loadingDoctor ||
                    doctorOptions.length === 0
                  }
                  onChange={(rows) => {
                    const list = Array.isArray(rows) ? rows : (rows ? [rows] : []);
                    const first = list[0];
                    if (first) {
                      setReferenceDoctor(String(first.id));
                    } else {
                      setReferenceDoctor("");
                    }
                  }}
                selectedId={referenceDoctor || undefined}
                  dialogMaxHeight="60vh"
                />
              </Box>
              <ByzMuiCheckBox
                text={t("patient-request.patient-file-change.reference-doctor-check")}
                checked={isReferenceDoctor}
              onChange={(e) => {
                setIsReferenceDoctor(e.target.checked);
                if (!e.target.checked) {
                  setReferenceDoctor("");
                }
              }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t("patient-request.patient-file-change.assistant-doctor")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <Box sx={{ width: 320 }}>
                <ByzSelectListPopupInput
                  fullWidth
                  size="small"
                  label={t("patient-request.patient-file-change.assistant-doctor")}
                value={selectedAssistantLabel}
                staticData={assistantStaticOptions}
                displayField="NAME"
                disabled={editOperationMode || !departmentName}
                onChange={(rows) => {
                  const list = Array.isArray(rows) ? rows : (rows ? [rows] : []);
                  const first = list[0];
                  if (first) {
                    setAssistantDoctor(String(first.id));
                  } else {
                    setAssistantDoctor("");
                  }
                }}
                  selectedId={assistantDoctor || undefined}
                dialogMaxHeight="60vh"
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t("patient-request.patient-file-change.referring-personnel")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <Box sx={{ width: 320 }}>
                <ByzSelectListPopupInput
                  fullWidth
                  size="small"
                  label={t("patient-request.patient-file-change.referring-personnel")}
                value={selectedReferringPersonnelLabel}
                staticData={referringPersonnelStaticOptions}
                displayField="NAME"
                disabled={editOperationMode || !departmentName}
                onChange={(rows) => {
                  const list = Array.isArray(rows) ? rows : (rows ? [rows] : []);
                  const first = list[0];
                  if (first) {
                    setReferringPersonnel(String(first.id));
                  } else {
                    setReferringPersonnel("");
                  }
                }}
                  selectedId={referringPersonnel || undefined}
                dialogMaxHeight="60vh"
              />
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ mt: 3 }} />
      </Box>
    </OpenComponentDialog>
  );
};

export default PatientComingFileChangeModal;
